import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useSeo } from "@/lib/useSeo";
import { Logo } from "@/components/SiteChrome";

const YEARS = ["1st", "2nd", "3rd", "4th", "PG"];

const SELF_DESCRIPTIONS = [
  "Student",
  "Recent Graduate",
  "Working Professional",
  "Entrepreneur",
  "Content Creator",
  "Influencer",
  "Brand / Company",
  "Other",
];

const INDUSTRIES = [
  "Technology / Software",
  "Marketing / Advertising",
  "Finance / Banking",
  "Education / EdTech",
  "Consulting",
  "Media / Entertainment",
  "Healthcare",
  "Design / Creative",
  "E-commerce / Retail",
  "Manufacturing",
  "Other",
];

const STARTUP_STAGES = [
  "Idea stage",
  "Building / Pre-launch",
  "Just launched",
  "Generating revenue",
  "Funded",
];

const CONTENT_NICHES = [
  "Tech / Programming",
  "Career / Education",
  "Lifestyle / Vlog",
  "Business / Entrepreneurship",
  "Finance / Investing",
  "Comedy / Entertainment",
  "Design / Art",
  "Fitness / Health",
  "Fashion / Beauty",
  "Travel",
  "Food",
  "Other",
];

const PRIMARY_PLATFORMS = ["Instagram", "LinkedIn", "YouTube", "Twitter / X", "Multiple platforms"];

const STEPS = [
  { id: 1, title: "Basics", desc: "Who you are" },
  { id: 2, title: "Reach", desc: "Your social presence" },
  { id: 3, title: "Story", desc: "Why you'd be great" },
  { id: 4, title: "Consent", desc: "Review & submit" },
];

const initial = {
  full_name: "",
  email: "",
  password: "",
  confirm_password: "",
  city_state: "",
  dashboard_handle: "",
  self_description: "",
  // student
  college: "",
  year_of_study: "",
  course: "",
  // graduate
  graduation_year: "",
  // working / entrepreneur
  company_name: "",
  role_title: "",
  industry: "",
  startup_stage: "",
  // creator / influencer
  content_niche: "",
  primary_platform: "",
  // brand / company
  brand_name: "",
  brand_website: "",
  brand_logo_path: "",
  brand_audience_size: "",
  // other
  custom_description: "",

  instagram_handle: "",
  instagram_followers: 0,
  linkedin_url: "",
  linkedin_followers: 0,
  youtube_url: "",
  youtube_subscribers: 0,
  twitter_handle: "",
  twitter_followers: 0,
  other_platform_name: "",
  other_platform_handle: "",
  other_platform_followers: 0,
  hackathons_experience: "",
  motivation: "",
  promotion_plan: "",
  expectations: "",
  reward_idea_1: "",
  reward_idea_2: "",
  reward_idea_3: "",
  consent_guidelines: false,
  consent_terms: false,
  consent_age: false,
  consent_dpdp: false,
  consent_follow_socials: false,
  public_display_consent: false,
  referrer_code: "",
};

function Field({ label, hint, children, required, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="mb-2 flex items-baseline justify-between">
        <span className="font-display text-sm font-semibold text-zinc-200">
          {label} {required && <span className="text-[#22D3EE]">*</span>}
        </span>
        {hint && <span className="text-xs text-zinc-500">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className={`w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5 text-sm text-white outline-none transition focus:border-[#A855F7] focus:ring-1 focus:ring-[#A855F7] ${props.className || ""}`}
    />
  );
}

function Textarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5 text-sm text-white outline-none transition focus:border-[#A855F7] focus:ring-1 focus:ring-[#A855F7] ${props.className || ""}`}
    />
  );
}

export default function Apply() {
  useSeo({
    title: "Apply — NoobSync Brand Ambassador Program",
    description: "Apply to become a NoobSync Brand Ambassador in minutes. Share your details and social handles — no login required. Founding-cohort spots are limited.",
    path: "/apply",
  });
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [dupe, setDupe] = useState(null);
  const [collectRewards, setCollectRewards] = useState(false);
  const [socials, setSocials] = useState({});
  const nav = useNavigate();

  useEffect(() => {
    api.get("/cohort/active")
      .then((r) => setCollectRewards(Boolean(r.data?.cohort?.collect_reward_ideas)))
      .catch(() => {});
    api.get("/site-settings")
      .then((r) => setSocials(r.data?.socials || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Capture ?ref=NSA-XXXX from URL (or fallback to a previously stored value).
    // We persist in localStorage so the code survives navigation back from /privacy etc.
    try {
      const fromUrl = new URLSearchParams(window.location.search).get("ref");
      const stored = localStorage.getItem("ns_ref");
      const ref = (fromUrl && /^NSA-/i.test(fromUrl)) ? fromUrl.toUpperCase() : (stored && /^NSA-/i.test(stored) ? stored : "");
      if (ref) {
        localStorage.setItem("ns_ref", ref);
        setData((d) => ({ ...d, referrer_code: ref }));
      }
    } catch (err) {
      console.warn("Failed to read ?ref param", err);
    }
  }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const validateStep = () => {
    if (step === 1) {
      if (!data.full_name.trim() || !data.email.trim() || !data.city_state.trim()) {
        toast.error("Fill all required fields");
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
        toast.error("Enter a valid email");
        return false;
      }
      if (data.password.length < 8 || !/[a-zA-Z]/.test(data.password) || !/[0-9]/.test(data.password)) {
        toast.error("Password must be at least 8 characters and include a letter and a number");
        return false;
      }
      if (data.password !== data.confirm_password) {
        toast.error("Passwords don't match");
        return false;
      }
    }
    if (step === 2) {
      if (!data.instagram_handle.trim() || data.instagram_followers < 0) {
        toast.error("Instagram handle and follower count are required");
        return false;
      }
    }
    if (step === 4) {
      if (
        !data.consent_guidelines ||
        !data.consent_terms ||
        !data.consent_age ||
        !data.consent_dpdp
      ) {
        toast.error("All four consents are required");
        return false;
      }
      if (!data.consent_follow_socials) {
        toast.error("Following NoobSync on our socials is required to join");
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    track("apply_step_completed", { step });
    setStep((s) => Math.min(4, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const payload = {
        full_name: data.full_name.trim(),
        email: data.email.trim().toLowerCase(),
        password: data.password,
        city_state: data.city_state.trim(),
        dashboard_handle: data.dashboard_handle.trim() || null,
        self_description: data.self_description || null,
        college: data.college.trim() || null,
        year_of_study: data.year_of_study || null,
        course: data.course.trim() || null,
        graduation_year: data.graduation_year.trim() || null,
        company_name: data.company_name.trim() || null,
        role_title: data.role_title.trim() || null,
        industry: data.industry || null,
        startup_stage: data.startup_stage || null,
        content_niche: data.content_niche || null,
        primary_platform: data.primary_platform || null,
        brand_name: data.brand_name.trim() || null,
        brand_website: data.brand_website.trim() || null,
        brand_logo_path: data.brand_logo_path || null,
        brand_audience_size: data.brand_audience_size.trim() || null,
        custom_description: data.custom_description.trim() || null,
        instagram_handle: data.instagram_handle.trim(),
        instagram_followers: Number(data.instagram_followers || 0),
        linkedin_url: data.linkedin_url.trim() || null,
        linkedin_followers: data.linkedin_url.trim() ? Number(data.linkedin_followers || 0) : null,
        youtube_url: data.youtube_url.trim() || null,
        youtube_subscribers: data.youtube_url.trim() ? Number(data.youtube_subscribers || 0) : null,
        twitter_handle: data.twitter_handle.trim() || null,
        twitter_followers: data.twitter_handle.trim() ? Number(data.twitter_followers || 0) : null,
        other_platform:
          data.other_platform_name.trim()
            ? {
                name: data.other_platform_name.trim(),
                handle: data.other_platform_handle.trim(),
                followers: Number(data.other_platform_followers || 0),
              }
            : null,
        hackathons_experience: data.hackathons_experience.trim() || null,
        motivation: data.motivation.trim() || null,
        promotion_plan: data.promotion_plan.trim() || null,
        expectations: data.expectations.trim() || null,
        reward_ideas: collectRewards ? (() => {
          const ideas = [data.reward_idea_1, data.reward_idea_2, data.reward_idea_3]
            .map((x) => (x || "").trim())
            .filter(Boolean);
          return ideas.length ? ideas.join(" | ").slice(0, 200) : null;
        })() : null,
        consent_guidelines: data.consent_guidelines,
        consent_terms: data.consent_terms,
        consent_age: data.consent_age,
        consent_dpdp: data.consent_dpdp,
        consent_follow_socials: data.consent_follow_socials,
        public_display_consent: data.public_display_consent,
        referrer_code: (data.referrer_code || "").trim() || null,
      };
      const res = await api.post("/applications", payload);
      setDone({ email: res.data.email, id: res.data.application_id });
      track("application_submitted", {
        application_id: res.data.application_id,
        has_referrer: Boolean(payload.referrer_code),
        primary_platform: payload.primary_platform || "none",
        self_description: payload.self_description || "none",
      });
      toast.success("Application submitted!");
    } catch (e) {
      if (e?.response?.status === 409) {
        setDupe({ email: data.email.trim().toLowerCase() });
        return;
      }
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (dupe) {
    return (
      <div className="min-h-screen bg-transparent text-white">
        <div className="mx-auto flex max-w-3xl flex-col items-center px-5 py-20 text-center">
          <Logo />
          <div className="mt-12 inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#22D3EE]/15 text-[#22D3EE]" data-testid="apply-already-applied">
            <Check className="h-8 w-8" strokeWidth={3} />
          </div>
          <h1 className="mt-6 font-display text-4xl font-black tracking-tighter">You've already applied</h1>
          <p className="mt-3 max-w-md text-zinc-400">
            Looks like there's already an application for{" "}
            <span className="font-mono text-[#22D3EE]">{dupe.email}</span>. We're reviewing it — keep an eye on your inbox.
          </p>
          <div className="mt-6 max-w-md rounded-xl border border-zinc-800 bg-zinc-950/70 p-5 text-left">
            <p className="text-sm leading-relaxed text-zinc-400">
              Already approved? You can sign in with the email and password you set — or request a one-time
              email code. No need to apply again.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/login" data-testid="dupe-go-signin" className="rounded-md bg-[#A855F7] px-6 py-3 text-sm font-bold text-white hover:-translate-y-0.5 transition-transform">
              Go to sign in
            </Link>
            <Link to="/" data-testid="dupe-back-home" className="rounded-md border border-zinc-800 px-6 py-3 text-sm text-zinc-300 hover:border-zinc-700 hover:text-white">
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-transparent text-white">
        <div className="mx-auto flex max-w-3xl flex-col items-center px-5 py-20 text-center">
          <Logo />
          <div className="mt-12 inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#A855F7] text-white" data-testid="apply-success">
            <Check className="h-8 w-8" strokeWidth={3} />
          </div>
          <h1 className="mt-6 font-display text-4xl font-black tracking-tighter">Application received</h1>
          <p className="mt-3 max-w-md text-zinc-400">
            Thanks for applying! Your application is now <span className="text-zinc-200">under review</span>. We'll email you at{" "}
            <span className="font-mono text-[#A855F7]">{done.email}</span> within 7 days.
          </p>
          <div className="mt-6 max-w-md rounded-xl border border-zinc-800 bg-zinc-950/70 p-5 text-left" data-testid="vetting-note-success">
            <p className="text-sm font-semibold text-zinc-200">Why we review every application</p>
            <p className="mt-2 text-sm leading-relaxed text-zinc-400">
              Each application is reviewed by a real person. This keeps the community genuine,
              prevents duplicate or fake profiles, and makes sure the leaderboard and rewards
              stay fair for everyone. Once you're approved, sign in with the email and password
              you just created — or request a one-time email code any time.
            </p>
          </div>
          <Link to="/" data-testid="apply-back-home" className="mt-10 rounded-md border border-zinc-800 px-6 py-3 text-sm text-zinc-300 hover:border-zinc-700 hover:text-white">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent text-white">
      <header className="border-b border-zinc-900">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-5">
          <Logo />
          <Link to="/" className="text-sm text-zinc-400 hover:text-white">Cancel</Link>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-5 py-10">
        <h1 className="mb-8 font-display text-3xl font-black tracking-tighter sm:text-4xl">
          Apply to become a <span className="brand-gradient-text">NoobSync</span> Brand Ambassador
        </h1>
        {/* Progress */}
        <div className="mb-10">
          <div className="flex items-center justify-between">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex flex-1 items-center" data-testid={`step-indicator-${s.id}`}>
                <div className={`flex h-9 w-9 items-center justify-center rounded-full font-mono text-sm font-bold ${step >= s.id ? "bg-[#A855F7] text-white" : "bg-zinc-900 text-zinc-500"}`}>
                  {step > s.id ? <Check className="h-4 w-4" /> : s.id}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`mx-2 h-px flex-1 ${step > s.id ? "bg-[#A855F7]" : "bg-zinc-800"}`} />
                )}
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs uppercase tracking-[0.2em] text-zinc-500">
            Step {step} of 4 — <span className="text-zinc-300">{STEPS[step - 1].title}</span>
          </p>
          {data.referrer_code && (
            <div data-testid="apply-referred-by" className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#A855F7]/40 bg-[#A855F7]/10 px-3 py-1 text-xs">
              <span className="text-zinc-400">Referred by</span>
              <span className="font-mono font-bold text-[#A855F7]">{data.referrer_code}</span>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-zinc-900 bg-zinc-950/60 p-6 sm:p-10">
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="font-display text-2xl font-bold">Tell us about you</h2>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full name" required testid="field-full-name">
                  <Input data-testid="input-full-name" value={data.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Your full name" />
                </Field>
                <Field label="City & country" required testid="field-city-state">
                  <Input data-testid="input-city-state" value={data.city_state} onChange={(e) => set("city_state", e.target.value)} placeholder="e.g. Bangalore, India · London, UK · Lagos, Nigeria" />
                </Field>
              </div>
              <Field label="Personal email" required testid="field-email" hint="Used to log in if accepted">
                <Input data-testid="input-email" type="email" value={data.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" />
              </Field>

              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Create a password" required testid="field-password" hint="Min 8 chars · 1 letter + 1 number">
                  <Input data-testid="input-password" type="password" value={data.password} onChange={(e) => set("password", e.target.value)} placeholder="Create a password" autoComplete="new-password" />
                </Field>
                <Field label="Confirm password" required testid="field-confirm-password">
                  <Input data-testid="input-confirm-password" type="password" value={data.confirm_password} onChange={(e) => set("confirm_password", e.target.value)} placeholder="Re-enter password" autoComplete="new-password" />
                </Field>
              </div>
              <p className="-mt-2 text-xs text-zinc-500" data-testid="password-note">
                You'll use this password to sign in once your application is approved. Forgot it later? You can always request a one-time email code instead.
              </p>

              <Field
                label="Public leaderboard alias"
                testid="field-dashboard-handle"
                hint="Optional · 3–24 chars · letters, numbers, _ and -. Want to stay anonymous? Pick a fun handle (e.g. neon_panda). Leave blank to use your first name + last initial."
              >
                <Input
                  data-testid="input-dashboard-handle"
                  value={data.dashboard_handle}
                  onChange={(e) => set("dashboard_handle", e.target.value.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 24))}
                  placeholder="neon_panda"
                  maxLength={24}
                />
              </Field>

              <Field label="How would you describe yourself?" testid="field-self-desc" hint="This personalizes the rest of the form">
                <select
                  data-testid="input-self-desc"
                  value={data.self_description}
                  onChange={(e) => set("self_description", e.target.value)}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5 text-sm text-white focus:border-[#A855F7] focus:outline-none focus:ring-1 focus:ring-[#A855F7]"
                >
                  <option value="">— Select —</option>
                  {SELF_DESCRIPTIONS.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </Field>

              {/* ===== Conditional fields by persona ===== */}
              {data.self_description === "Student" && (
                <div className="rounded-lg border border-zinc-900 bg-zinc-950/60 p-4 space-y-4" data-testid="block-student">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#A855F7]">Student details</p>
                  <Field label="College name" testid="field-college" hint="Optional">
                    <Input data-testid="input-college" value={data.college} onChange={(e) => set("college", e.target.value)} placeholder="Your college / university" />
                  </Field>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Year of study" testid="field-year" hint="Optional">
                      <select data-testid="input-year" value={data.year_of_study} onChange={(e) => set("year_of_study", e.target.value)} className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5 text-sm focus:border-[#A855F7] focus:outline-none focus:ring-1 focus:ring-[#A855F7]">
                        <option value="">— Select —</option>
                        {YEARS.map((y) => (<option key={y} value={y}>{y}</option>))}
                      </select>
                    </Field>
                    <Field label="Course / Branch" testid="field-course" hint="Optional">
                      <Input data-testid="input-course" value={data.course} onChange={(e) => set("course", e.target.value)} placeholder="e.g. CS, Bachelor's, MBA" />
                    </Field>
                  </div>
                </div>
              )}

              {data.self_description === "Recent Graduate" && (
                <div className="rounded-lg border border-zinc-900 bg-zinc-950/60 p-4 space-y-4" data-testid="block-graduate">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#A855F7]">Education details</p>
                  <Field label="College / University" testid="field-college" hint="Optional">
                    <Input data-testid="input-college" value={data.college} onChange={(e) => set("college", e.target.value)} placeholder="Where you graduated" />
                  </Field>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Graduation year" testid="field-grad-year" hint="Optional">
                      <Input data-testid="input-grad-year" value={data.graduation_year} onChange={(e) => set("graduation_year", e.target.value)} placeholder="2024" />
                    </Field>
                    <Field label="Course / Degree" testid="field-course" hint="Optional">
                      <Input data-testid="input-course" value={data.course} onChange={(e) => set("course", e.target.value)} placeholder="e.g. CS, Bachelor's, MBA" />
                    </Field>
                  </div>
                </div>
              )}

              {data.self_description === "Working Professional" && (
                <div className="rounded-lg border border-zinc-900 bg-zinc-950/60 p-4 space-y-4" data-testid="block-working">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#A855F7]">Work details</p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Company" testid="field-company" hint="Optional">
                      <Input data-testid="input-company" value={data.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Acme Inc." />
                    </Field>
                    <Field label="Role / Title" testid="field-role" hint="Optional">
                      <Input data-testid="input-role" value={data.role_title} onChange={(e) => set("role_title", e.target.value)} placeholder="Software Engineer" />
                    </Field>
                  </div>
                  <Field label="Industry" testid="field-industry" hint="Optional">
                    <select data-testid="input-industry" value={data.industry} onChange={(e) => set("industry", e.target.value)} className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5 text-sm focus:border-[#A855F7] focus:outline-none focus:ring-1 focus:ring-[#A855F7]">
                      <option value="">— Select —</option>
                      {INDUSTRIES.map((i) => (<option key={i} value={i}>{i}</option>))}
                    </select>
                  </Field>
                </div>
              )}

              {data.self_description === "Entrepreneur" && (
                <div className="rounded-lg border border-zinc-900 bg-zinc-950/60 p-4 space-y-4" data-testid="block-entrepreneur">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#A855F7]">Startup details</p>
                  <Field label="Startup / Company name" testid="field-company" hint="Optional">
                    <Input data-testid="input-company" value={data.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="What are you building?" />
                  </Field>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Industry" testid="field-industry" hint="Optional">
                      <select data-testid="input-industry" value={data.industry} onChange={(e) => set("industry", e.target.value)} className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5 text-sm focus:border-[#A855F7] focus:outline-none focus:ring-1 focus:ring-[#A855F7]">
                        <option value="">— Select —</option>
                        {INDUSTRIES.map((i) => (<option key={i} value={i}>{i}</option>))}
                      </select>
                    </Field>
                    <Field label="Stage" testid="field-stage" hint="Optional">
                      <select data-testid="input-stage" value={data.startup_stage} onChange={(e) => set("startup_stage", e.target.value)} className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5 text-sm focus:border-[#A855F7] focus:outline-none focus:ring-1 focus:ring-[#A855F7]">
                        <option value="">— Select —</option>
                        {STARTUP_STAGES.map((s) => (<option key={s} value={s}>{s}</option>))}
                      </select>
                    </Field>
                  </div>
                </div>
              )}

              {(data.self_description === "Content Creator" || data.self_description === "Influencer") && (
                <div className="rounded-lg border border-zinc-900 bg-zinc-950/60 p-4 space-y-4" data-testid="block-creator">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#A855F7]">Creator details</p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Primary content niche" testid="field-niche" hint="Optional">
                      <select data-testid="input-niche" value={data.content_niche} onChange={(e) => set("content_niche", e.target.value)} className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5 text-sm focus:border-[#A855F7] focus:outline-none focus:ring-1 focus:ring-[#A855F7]">
                        <option value="">— Select —</option>
                        {CONTENT_NICHES.map((n) => (<option key={n} value={n}>{n}</option>))}
                      </select>
                    </Field>
                    <Field label="Primary platform" testid="field-platform" hint="Optional">
                      <select data-testid="input-platform" value={data.primary_platform} onChange={(e) => set("primary_platform", e.target.value)} className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5 text-sm focus:border-[#A855F7] focus:outline-none focus:ring-1 focus:ring-[#A855F7]">
                        <option value="">— Select —</option>
                        {PRIMARY_PLATFORMS.map((p) => (<option key={p} value={p}>{p}</option>))}
                      </select>
                    </Field>
                  </div>
                </div>
              )}

              {data.self_description === "Brand / Company" && (
                <div className="rounded-lg border border-zinc-900 bg-zinc-950/60 p-4 space-y-4" data-testid="block-brand">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#A855F7]">Brand details</p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Brand / Company name" testid="field-brand-name" hint="Optional">
                      <Input data-testid="input-brand-name" value={data.brand_name} onChange={(e) => set("brand_name", e.target.value)} placeholder="Your brand name" />
                    </Field>
                    <Field label="Website" testid="field-brand-web" hint="Optional">
                      <Input data-testid="input-brand-web" value={data.brand_website} onChange={(e) => set("brand_website", e.target.value)} placeholder="https://yourbrand.com" />
                    </Field>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Industry" testid="field-industry" hint="Optional">
                      <select data-testid="input-industry" value={data.industry} onChange={(e) => set("industry", e.target.value)} className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5 text-sm focus:border-[#A855F7] focus:outline-none focus:ring-1 focus:ring-[#A855F7]">
                        <option value="">— Select —</option>
                        {INDUSTRIES.map((i) => (<option key={i} value={i}>{i}</option>))}
                      </select>
                    </Field>
                    <Field label="Community / audience size" testid="field-brand-aud" hint="Optional — total reach">
                      <Input data-testid="input-brand-aud" value={data.brand_audience_size} onChange={(e) => set("brand_audience_size", e.target.value)} placeholder="e.g. 50K followers, 10K newsletter" />
                    </Field>
                  </div>
                  <BrandLogoField path={data.brand_logo_path} onChange={(p) => set("brand_logo_path", p)} />
                </div>
              )}

              {data.self_description === "Other" && (
                <div className="rounded-lg border border-zinc-900 bg-zinc-950/60 p-4 space-y-4" data-testid="block-other">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#A855F7]">Tell us more</p>
                  <Field label="Describe what you do" testid="field-custom" hint={`${data.custom_description.length}/200`}>
                    <Textarea data-testid="input-custom" rows={3} maxLength={200} value={data.custom_description} onChange={(e) => set("custom_description", e.target.value)} placeholder="In a few words…" />
                  </Field>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <h2 className="font-display text-2xl font-bold">Your reach</h2>
              <p className="text-sm text-zinc-500">Instagram is required. Everything else is optional — share what you have.</p>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Instagram handle" required testid="field-ig">
                  <Input data-testid="input-ig-handle" value={data.instagram_handle} onChange={(e) => set("instagram_handle", e.target.value)} placeholder="@your.handle" />
                </Field>
                <Field label="Instagram followers" required testid="field-ig-followers">
                  <Input data-testid="input-ig-followers" type="number" min={0} value={data.instagram_followers} onChange={(e) => set("instagram_followers", e.target.value)} />
                </Field>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="LinkedIn URL" testid="field-li" hint="Optional">
                  <Input data-testid="input-li-url" value={data.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)} placeholder="https://linkedin.com/in/..." />
                </Field>
                <Field label="LinkedIn followers/connections" testid="field-li-followers" hint="Optional">
                  <Input data-testid="input-li-followers" type="number" min={0} value={data.linkedin_followers} onChange={(e) => set("linkedin_followers", e.target.value)} />
                </Field>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="YouTube channel URL" testid="field-yt" hint="Optional">
                  <Input data-testid="input-yt-url" value={data.youtube_url} onChange={(e) => set("youtube_url", e.target.value)} placeholder="https://youtube.com/@..." />
                </Field>
                <Field label="YouTube subscribers" testid="field-yt-subs" hint="Optional">
                  <Input data-testid="input-yt-subs" type="number" min={0} value={data.youtube_subscribers} onChange={(e) => set("youtube_subscribers", e.target.value)} />
                </Field>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Twitter / X handle" testid="field-tw" hint="Optional">
                  <Input data-testid="input-tw-handle" value={data.twitter_handle} onChange={(e) => set("twitter_handle", e.target.value)} placeholder="@..." />
                </Field>
                <Field label="Twitter / X followers" testid="field-tw-followers" hint="Optional">
                  <Input data-testid="input-tw-followers" type="number" min={0} value={data.twitter_followers} onChange={(e) => set("twitter_followers", e.target.value)} />
                </Field>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Other platform" testid="field-other-name">
                  <Input data-testid="input-other-name" value={data.other_platform_name} onChange={(e) => set("other_platform_name", e.target.value)} placeholder="Threads" />
                </Field>
                <Field label="Handle" testid="field-other-handle">
                  <Input data-testid="input-other-handle" value={data.other_platform_handle} onChange={(e) => set("other_platform_handle", e.target.value)} placeholder="@..." />
                </Field>
                <Field label="Followers" testid="field-other-followers">
                  <Input data-testid="input-other-followers" type="number" min={0} value={data.other_platform_followers} onChange={(e) => set("other_platform_followers", e.target.value)} />
                </Field>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <h2 className="font-display text-2xl font-bold">Your story</h2>
              <p className="text-sm text-zinc-500">All optional — but a good story really helps.</p>
              <Field label="Hackathons / tech events" hint={`${data.hackathons_experience.length}/200`} testid="field-hack">
                <Textarea data-testid="input-hack" rows={3} maxLength={200} value={data.hackathons_experience} onChange={(e) => set("hackathons_experience", e.target.value)} placeholder="e.g. HackMIT semifinalist, ETHGlobal Lisbon winner, Smart India Hackathon finalist…" />
              </Field>
              <Field label="Why do you want to be a NoobSync Brand Ambassador?" hint={`${data.motivation.length}/300`} testid="field-motivation">
                <Textarea data-testid="input-motivation" rows={4} maxLength={300} value={data.motivation} onChange={(e) => set("motivation", e.target.value)} />
              </Field>
              <Field label="What do you expect from this program?" hint={`${data.expectations.length}/300 · e.g. mentorship, network, portfolio wins, founder access`} testid="field-expectations">
                <Textarea data-testid="input-expectations" rows={3} maxLength={300} value={data.expectations} onChange={(e) => set("expectations", e.target.value)} placeholder="What would make this 6-month journey worth your time? Career outcomes, learnings, connections, money — be honest." />
              </Field>
              <Field label="How will you promote NoobSync to your audience?" hint={`${data.promotion_plan.length}/300`} testid="field-promo">
                <Textarea data-testid="input-promo" rows={4} maxLength={300} value={data.promotion_plan} onChange={(e) => set("promotion_plan", e.target.value)} />
              </Field>
              {collectRewards && (
              <Field label="What rewards would excite you most?" hint="Suggest up to 3 — your input shapes our reward pool" testid="field-rewards">
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2 rounded-lg border border-[#A855F7]/20 bg-[#A855F7]/5 p-3 text-xs text-zinc-400">
                    <svg className="h-4 w-4 shrink-0 text-[#A855F7]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18M3 12h18" /></svg>
                    <span>Your suggestions <span className="font-bold text-zinc-200">help shape our reward pool</span>. We weigh what&apos;s most useful, motivating &amp; realistic when planning the next cohort — practical picks tend to land more often, but bold long-shots are welcome too. Be specific (e.g. &ldquo;GoPro Hero 12&rdquo;, &ldquo;AWS credits&rdquo;, &ldquo;1:1 with NoobSync founders&rdquo;, &ldquo;Cash ₹10k&rdquo;).</span>
                  </div>
                  <Input
                    data-testid="input-reward-1"
                    maxLength={80}
                    value={data.reward_idea_1}
                    onChange={(e) => set("reward_idea_1", e.target.value)}
                    placeholder="Reward idea #1"
                  />
                  <Input
                    data-testid="input-reward-2"
                    maxLength={80}
                    value={data.reward_idea_2}
                    onChange={(e) => set("reward_idea_2", e.target.value)}
                    placeholder="Reward idea #2 (optional)"
                  />
                  <Input
                    data-testid="input-reward-3"
                    maxLength={80}
                    value={data.reward_idea_3}
                    onChange={(e) => set("reward_idea_3", e.target.value)}
                    placeholder="Reward idea #3 (optional)"
                  />
                </div>
              </Field>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <h2 className="font-display text-2xl font-bold">Review & consent</h2>
              <div className="rounded-lg border border-[#22D3EE]/25 bg-[#22D3EE]/[0.04] p-4" data-testid="vetting-note-review">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#22D3EE]">A quick note on review</p>
                <p className="mt-2 text-sm leading-relaxed text-zinc-300">
                  Every application is reviewed by a real person before approval. This human check keeps the
                  program authentic, blocks duplicate or fake accounts, and keeps points, rankings and rewards
                  fair for the whole community. Most applicants hear back within 7 days.
                </p>
              </div>
              <div className="rounded-lg border border-zinc-900 bg-zinc-950 p-5 text-sm">
                <p className="font-display font-semibold">{data.full_name}</p>
                <p className="text-zinc-500">{data.email}</p>
                <p className="mt-2 text-zinc-400">
                  {data.self_description || "—"} • {data.city_state}
                </p>
                {data.college && <p className="mt-1 text-zinc-500">{[data.college, data.year_of_study, data.course].filter(Boolean).join(" • ")}</p>}
                {data.company_name && <p className="mt-1 text-zinc-500">{[data.company_name, data.role_title, data.industry].filter(Boolean).join(" • ")}</p>}
                {data.brand_name && <p className="mt-1 text-zinc-500">{[data.brand_name, data.brand_website, data.brand_audience_size].filter(Boolean).join(" • ")}</p>}
                {data.content_niche && <p className="mt-1 text-zinc-500">{[data.content_niche, data.primary_platform].filter(Boolean).join(" • ")}</p>}
                <p className="mt-3 text-zinc-500">
                  IG: {data.instagram_handle} • {data.instagram_followers}
                  {data.linkedin_url && ` • LinkedIn: ${data.linkedin_followers || 0}`}
                  {data.youtube_url && ` • YouTube: ${data.youtube_subscribers || 0}`}
                  {data.twitter_handle && ` • X: ${data.twitter_followers || 0}`}
                </p>
              </div>
              <label data-testid="consent-guidelines" className="flex items-start gap-3 rounded-lg border border-zinc-900 bg-zinc-950 p-4">
                <input type="checkbox" data-testid="consent-guidelines-input" className="mt-1 h-4 w-4 accent-[#A855F7]" checked={data.consent_guidelines} onChange={(e) => set("consent_guidelines", e.target.checked)} />
                <span className="text-sm text-zinc-300">
                  I have read and agree to the{" "}
                  <Link to="/guidelines" target="_blank" rel="noreferrer" className="text-[#A855F7] underline-offset-2 hover:underline">Brand Ambassador Guidelines</Link>.
                </span>
              </label>
              <label data-testid="consent-terms" className="flex items-start gap-3 rounded-lg border border-zinc-900 bg-zinc-950 p-4">
                <input type="checkbox" data-testid="consent-terms-input" className="mt-1 h-4 w-4 accent-[#A855F7]" checked={data.consent_terms} onChange={(e) => set("consent_terms", e.target.checked)} />
                <span className="text-sm text-zinc-300">
                  I accept the{" "}
                  <Link to="/terms" target="_blank" rel="noreferrer" className="text-[#A855F7] underline-offset-2 hover:underline">Ambassador Program Terms & Conditions</Link>.
                </span>
              </label>
              <label data-testid="consent-age" className="flex items-start gap-3 rounded-lg border border-zinc-900 bg-zinc-950 p-4">
                <input type="checkbox" data-testid="consent-age-input" className="mt-1 h-4 w-4 accent-[#A855F7]" checked={data.consent_age} onChange={(e) => set("consent_age", e.target.checked)} />
                <span className="text-sm text-zinc-300">
                  I confirm I am 18 years of age or older. If under 18, I have attached parental/guardian consent (email{" "}
                  <a href="mailto:info@noobsync.com" className="text-[#A855F7]">info@noobsync.com</a>{" "}
                  separately).
                </span>
              </label>
              <label data-testid="consent-dpdp" className="flex items-start gap-3 rounded-lg border border-zinc-900 bg-zinc-950 p-4">
                <input type="checkbox" data-testid="consent-dpdp-input" className="mt-1 h-4 w-4 accent-[#A855F7]" checked={data.consent_dpdp} onChange={(e) => set("consent_dpdp", e.target.checked)} />
                <span className="text-sm text-zinc-300">
                  I consent to NoobSync storing and processing my personal data as described in the{" "}
                  <Link to="/privacy" target="_blank" rel="noreferrer" className="text-[#A855F7] underline-offset-2 hover:underline">Privacy Policy</Link>{" "}
                  (DPDP Act 2023 compliant).
                </span>
              </label>

              <div data-testid="consent-follow" className="rounded-lg border border-[#22D3EE]/30 bg-[#22D3EE]/[0.05] p-4">
                <label className="flex items-start gap-3">
                  <input type="checkbox" data-testid="consent-follow-input" className="mt-1 h-4 w-4 accent-[#22D3EE]" checked={data.consent_follow_socials} onChange={(e) => set("consent_follow_socials", e.target.checked)} />
                  <span className="text-sm text-zinc-200">
                    <span className="font-semibold">I follow NoobSync on Instagram, YouTube &amp; LinkedIn</span>{" "}
                    <span className="text-[#22D3EE]">(required)</span> — being part of the community starts here. We verify this during review.
                  </span>
                </label>
                <div className="mt-3 flex flex-wrap gap-2 pl-7" data-testid="follow-links">
                  {[["instagram", "Instagram"], ["youtube", "YouTube"], ["linkedin", "LinkedIn"]].map(([k, label]) => {
                    const url = socials?.[k]?.url;
                    if (!url) return null;
                    return (
                      <a
                        key={k}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`follow-link-${k}`}
                        className="inline-flex items-center gap-1 rounded-full border border-[#22D3EE]/40 bg-[#22D3EE]/10 px-3 py-1 text-xs font-semibold text-[#22D3EE] transition hover:bg-[#22D3EE]/20"
                      >
                        Follow on {label} ↗
                      </a>
                    );
                  })}
                </div>
              </div>

              <div className="mt-3 rounded-lg border border-[#A855F7]/25 bg-[#A855F7]/[0.04] p-4">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A855F7]">Optional</p>
                <label data-testid="public-display-consent" className="mt-2 flex items-start gap-3">
                  <input type="checkbox" data-testid="public-display-consent-input" className="mt-1 h-4 w-4 accent-[#A855F7]" checked={data.public_display_consent} onChange={(e) => set("public_display_consent", e.target.checked)} />
                  <span className="text-sm text-zinc-300">
                    Show my name and profile details (e.g. college / company / brand) on the public Cohort Results page if I rank in the Top&nbsp;10%.{" "}
                    <span className="text-zinc-500">If unchecked you&apos;ll appear anonymously as &quot;Ambassador #N&quot;. You can change this later by emailing info@noobsync.com.</span>
                  </span>
                </label>
              </div>
            </div>
          )}

          <div className="mt-10 flex items-center justify-between">
            <button
              data-testid="step-back-btn"
              onClick={back}
              disabled={step === 1}
              className="inline-flex items-center gap-2 rounded-md border border-zinc-800 px-5 py-2.5 text-sm text-zinc-300 disabled:opacity-40 hover:border-zinc-700 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            {step < 4 ? (
              <button
                data-testid="step-next-btn"
                onClick={next}
                className="inline-flex items-center gap-2 rounded-md bg-[#A855F7] px-6 py-2.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5"
              >
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                data-testid="apply-submit-btn"
                onClick={submit}
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-md bg-[#A855F7] px-6 py-2.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Submit application
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function BrandLogoField({ path, onChange }) {
  const [uploading, setUploading] = useState(false);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be ≤ 2 MB");
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      setUploading(true);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/brand-logo`, {
        method: "POST",
        body: fd,
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json.detail || "Upload failed");
      onChange(json.path);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid="brand-logo-field">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Brand logo (optional)</p>
      <div className="flex items-center gap-3">
        {path ? (
          <div className="rounded-md border border-zinc-800 bg-white p-1.5">
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`} alt="logo" className="h-16 w-16 object-contain" />
          </div>
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-md border border-dashed border-zinc-700 text-xs text-zinc-600">No logo</div>
        )}
        <div>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-zinc-200 hover:border-[#A855F7]/60">
            {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            {path ? "Replace logo" : "Upload logo"}
            <input data-testid="brand-logo-input" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={onPick} disabled={uploading} />
          </label>
          {path && (
            <button type="button" onClick={() => onChange("")} className="ml-2 text-xs text-rose-400 hover:underline" data-testid="brand-logo-remove">
              Remove
            </button>
          )}
          <p className="mt-1 text-[10px] text-zinc-600">PNG, JPG, WEBP or SVG · max 2 MB · shown on the public Cohort Results page.</p>
        </div>
      </div>
    </div>
  );
}

